import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Paper,
  Modal,
  Fade,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LogoPrefeitura from "../../assets/images/logo-prefeitura.png";
import { cadastrarCandidato, loginCandidato } from "../../services/candidato";
import {
  normalizarNome,
  validarNome,
  formatarCpf,
  validarCpf,
} from "../../utils/normalization";
import { useAuth } from "../../contexts/AuthContext";
import type { LoginRequest } from "../../types";

const CadastrarCandidatos: React.FC = () => {
  const [mensagem, setMensagem] = useState<string>("");
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [telefone, setTelefone] = useState("");
  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  // const [cnh, setCnh] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  const [openModal, setOpenModal] = useState(false);
  const [candidatoCadastradoId, setCandidatoCadastradoId] = useState<
    number | null
  >(null);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleCloseModal = () => {
    setOpenModal(false);
    setTimeout(() => {
      if (candidatoCadastradoId !== null) {
        navigate("/painel-vagas", {
          state: { candidatoId: candidatoCadastradoId },
        });
      } else {
        navigate("/painel-vagas");
      }
    }, 300);
  };

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
    setErrorMessage("");
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");

    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return numbers.replace(/^(\d{2})(\d{0,5})$/, "($1) $2");
    } else if (numbers.length <= 11) {
      return numbers.replace(/^(\d{2})(\d{5})(\d{0,4})$/, "($1) $2-$3");
    }

    return numbers
      .slice(0, 11)
      .replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar nome - não pode conter números
    if (!validarNome(nome)) {
      setMensagem(
        "Nome inválido! O nome não pode conter números ou caracteres especiais.",
      );
      return;
    }

    // Validar CPF
    const cpfNumerico = cpf.replace(/\D/g, "");
    if (!validarCpf(cpfNumerico)) {
      setErrorMessage("CPF inválido! Por favor, insira um CPF válido.");
      setOpenErrorModal(true);
      return;
    }

    // Validar se as senhas coincidem
    if (senha !== confirmarSenha) {
      setErrorMessage("As senhas não coincidem!");
      setOpenErrorModal(true);
      return;
    }

    // Remover formatação do telefone antes de enviar
    const telefoneNumerico = telefone.replace(/\D/g, "");

    const novoCandidato = {
      nome: normalizarNome(nome),
      cpf: cpfNumerico,
      telefone: telefoneNumerico,
      senha: senha,
    };

    try {
      const response = await cadastrarCandidato(novoCandidato);
      if (response.data?.id) {
        // Verificar se o candidato retornado está ativo
        const candidatoStatus = response.data.status || "ativo"; // assumir ativo se não especificado

        if (candidatoStatus === "inativo") {
          setErrorMessage(
            "USUÁRIO INATIVO\n\nSua conta foi inativada. Para reativar seu acesso, procure a Prefeitura Local.",
          );
          setOpenErrorModal(true);
          return;
        }

        if (response.status === 201 || response.status === 200) {
          // Se status 200, cadastro já existia - redirecionar para login
          if (response.status === 200) {
            setMensagem("Cadastro já existe. Redirecionando para o login...");
            setOpenModal(true);
            setTimeout(() => {
              navigate("/login-candidato");
            }, 3500);
            return;
          }

          // Status 201 - Cadastro novo, fazer login automaticamente
          try {
            const loginData: LoginRequest = {
              cpf: cpfNumerico,
              senha: senha,
            };

            const loginResponse = await loginCandidato(loginData);

            if (loginResponse.data?.success && loginResponse.data?.data) {
              const loginInfo = loginResponse.data.data;

              // Usar o AuthContext para fazer login
              const userData = {
                id: loginInfo.id,
                nome: loginInfo.nome,
                email: loginInfo.email,
                telefone: loginInfo.telefone,
                tipo: "candidato" as const,
                cpf: cpfNumerico,
              };

              // Salvar no AuthContext
              login(loginInfo.token, userData);

              setCandidatoCadastradoId(loginInfo.id);
              setMensagem("Cadastro realizado com sucesso!");
              setOpenModal(true);
            } else {
              // Se o login falhar, apenas salva o ID no sessionStorage
              setCandidatoCadastradoId(response.data.id);
              sessionStorage.setItem(
                "candidatoId",
                response.data.id.toString(),
              );
              setMensagem("Cadastro realizado com sucesso!");
              setOpenModal(true);
            }
          } catch (loginError) {
            // Se o login falhar, apenas salva o ID no sessionStorage
            console.error("Erro ao fazer login após cadastro:", loginError);
            setCandidatoCadastradoId(response.data.id);
            sessionStorage.setItem("candidatoId", response.data.id.toString());
            setMensagem("Cadastro realizado com sucesso!");
            setOpenModal(true);
          }
        } else {
          setErrorMessage(
            "Cadastro efetuado, mas não foi possível obter o ID.",
          );
          setOpenErrorModal(true);
        }
      }
    } catch (error: any) {
      console.error("Erro ao cadastrar candidato:", error);

      // Verificar se o erro é específico de candidato inativo
      if (
        error?.response?.status === 403 ||
        (error?.response?.data &&
          (error.response.data.includes("inativo") ||
            error.response.data.includes("INATIVO")))
      ) {
        setErrorMessage(
          "❌ USUÁRIO INATIVO\n\nSua conta foi inativada. Para reativar seu acesso, procure a Prefeitura Local.",
        );
      } else {
        const msg = error?.response?.data || "Erro desconhecido ao cadastrar.";
        setErrorMessage(msg);
      }
      setOpenErrorModal(true);
    }
  };

  const isFormValid = () => {
    const phoneNumbers = telefone.replace(/\D/g, "");
    const cpfNumbers = cpf.replace(/\D/g, "");
    return (
      nome.trim() !== "" &&
      cpfNumbers.length === 11 &&
      telefone.trim() !== "" &&
      phoneNumbers.length >= 10 &&
      phoneNumbers.length <= 11 &&
      senha.trim().length >= 6 &&
      confirmarSenha.trim().length >= 6 &&
      senha === confirmarSenha
      // && (cnh === "sim" || cnh === "nao")
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 5,
            pt: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 2,
            backgroundColor: "#ffffff",
            maxWidth: 450,
            margin: "0 auto",
            minHeight: 750,
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={LogoPrefeitura}
            alt="Logo Prefeitura"
            sx={{
              width: 240,
              height: "auto",
              mb: 3,
              objectFit: "contain",
            }}
          />

          {/* Título */}
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, mb: 0.5, color: "#333" }}
          >
            Portal de Vagas
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, color: "#666" }}>
            Prefeitura de Bom Retiro do Sul
          </Typography>

          {/* Formulário */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              mt: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box>
              <TextField
                fullWidth
                label="Nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                margin="normal"
                required
                variant="outlined"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "0.875rem",
                    "&.Mui-focused": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px 14px",
                    color: "#333",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="CPF"
                value={cpf}
                onChange={(e) => {
                  const formatted = formatarCpf(e.target.value);
                  setCpf(formatted);
                }}
                margin="normal"
                required
                placeholder="000.000.000-00"
                variant="outlined"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "0.875rem",
                    "&.Mui-focused": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px 14px",
                    color: "#333",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Telefone"
                value={telefone}
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  setTelefone(formatted);
                }}
                margin="normal"
                required
                placeholder="(51) 99999-8080"
                variant="outlined"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "0.875rem",
                    "&.Mui-focused": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px 14px",
                    color: "#333",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Senha"
                type={showPassword ? "text" : "password"}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                margin="normal"
                required
                placeholder="Mínimo 6 caracteres"
                variant="outlined"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "0.875rem",
                    "&.Mui-focused": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px 14px",
                    color: "#333",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: "#666" }}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirmar Senha"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmarSenha}
                onChange={(e) => setConfirmarSenha(e.target.value)}
                margin="normal"
                required
                placeholder="Digite a senha novamente"
                variant="outlined"
                error={confirmarSenha.length > 0 && senha !== confirmarSenha}
                helperText={
                  confirmarSenha.length > 0 && senha !== confirmarSenha
                    ? "As senhas não coincidem"
                    : ""
                }
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "0.875rem",
                    "&.Mui-focused": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px 14px",
                    color: "#333",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        size="small"
                        sx={{ color: "#666" }}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* FormControl component="fieldset" margin="normal" sx={{ mb: 3 }}>
                <FormLabel
                  component="legend"
                  sx={{ color: "#666", fontSize: "0.875rem", mb: 1 }}
                >
                  Possui CNH*
                </FormLabel>
                <RadioGroup
                  row
                  value={cnh}
                  onChange={(e) => setCnh(e.target.value)}
                >
                  <FormControlLabel
                    value="sim"
                    control={
                      <Radio
                        sx={{
                          color: "#666",
                          "&.Mui-checked": { color: "#1976d2" },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ color: "#333", fontSize: "0.875rem" }}>
                        Sim
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="nao"
                    control={
                      <Radio
                        sx={{
                          color: "#666",
                          "&.Mui-checked": { color: "#1976d2" },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ color: "#333", fontSize: "0.875rem" }}>
                        Não
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl> */}
            </Box>

            <Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={!isFormValid()}
                sx={{
                  mt: 1,
                  mb: 2,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  backgroundColor: "#2196f3",
                  boxShadow: "none",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1976d2",
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#999",
                  },
                }}
              >
                CADASTRAR
              </Button>

              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                  Sou candidato?{" "}
                  <Button
                    variant="text"
                    onClick={() => navigate("/login-candidato")}
                    sx={{
                      textTransform: "none",
                      color: "#1976d2",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Fazer Login
                  </Button>
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Sou empresa?{" "}
                  <Button
                    variant="text"
                    onClick={() => navigate("/login-empresa")}
                    sx={{
                      textTransform: "none",
                      color: "#1976d2",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Fazer Login
                  </Button>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openModal}
        onClose={
          mensagem.includes("Redirecionando") ? () => {} : handleCloseModal
        }
        closeAfterTransition
        BackdropProps={{
          sx: {
            backdropFilter: "blur(5px)",
            bgcolor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 60, color: "success.main", mb: 2 }}
            />
            <Typography
              id="transition-modal-title"
              variant="h6"
              component="h2"
              fontWeight="bold"
            >
              Sucesso!
            </Typography>
            <Typography id="transition-modal-description" sx={{ mt: 2 }}>
              {mensagem}
            </Typography>
            {!mensagem.includes("Redirecionando") && (
              <Button
                variant="contained"
                onClick={handleCloseModal}
                sx={{ mt: 3 }}
              >
                OK
              </Button>
            )}
          </Box>
        </Fade>
      </Modal>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openErrorModal}
        onClose={handleCloseErrorModal}
        closeAfterTransition
        BackdropProps={{
          sx: {
            backdropFilter: "blur(5px)",
            bgcolor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <Fade in={openErrorModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <ErrorOutlineIcon
              sx={{ fontSize: 60, color: "error.main", mb: 2 }}
            />
            <Typography
              id="transition-modal-title"
              variant="h6"
              component="h2"
              fontWeight="bold"
            >
              Erro no Cadastro
            </Typography>
            <Typography id="transition-modal-description" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              onClick={handleCloseErrorModal}
              sx={{ mt: 3 }}
            >
              Fechar
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default CadastrarCandidatos;
